/**
 * @fileoverview The gallery queue, for whoever approves.
 *
 * Three lists — waiting, on the website, hidden — and two buttons per
 * photo. Approving copies the picture somewhere public; hiding takes it
 * down. Only approvers are offered this page; anyone else gets told so.
 * A press that the server refuses is said in a toast; it used to put the
 * buttons back with nothing else. A list that could not be read says so,
 * rather than "Nothing waiting".
 *
 * Where: the route /admin/gallery (App.tsx), inside the Layout; linked from
 * the header's menu for approvers.
 * API: GET /gallery/mine (useMyGallery, for `approver`); GET
 * /gallery/queue?status= per tab; POST /gallery/:id/approve and POST
 * /gallery/:id/hide.
 * Settings: GALLERY_QUEUE_QUERY_KEY (config/tunables/garden); who approves
 * is the backend's.
 * Words (en/es): gallery.queue.*. A failure on our side (a 500, no
 * connection; D31, lib/serverFailure), in a read or a press, says the
 * standard sentence, reported to Sentry, with the support address as a link
 * (lib/errorMessage), in place of gallery.queue.loadFailed and the toasts'
 * own words.
 * Watch out: the page is open to anyone signed in; the server refuses the
 * queue to non-approvers, and this page only words that.
 *
 * @module GalleryQueue
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, EyeOff } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { useToast } from '@/shared/components/ui/use-toast';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import {
  approveGalleryItem, getGalleryQueue, hideGalleryItem, type GalleryStatus,
} from '@/shared/api/gallery';
import { useMyGallery } from './lib/useMyGallery';
import { GALLERY_QUEUE_QUERY_KEY } from '@/config/tunables/garden';

/** The queue's three lists, in the order of their tabs. */
const TABS: GalleryStatus[] = ['pending', 'approved', 'hidden'];

/** The approver's queue: a tab per status, a row per photo with Approve and Hide. */
export const GalleryQueue: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: mine } = useMyGallery();
  const [tab, setTab] = React.useState<GalleryStatus>('pending');
  const { data: items = [], isLoading, isError, error } = useQuery({
    queryKey: [...GALLERY_QUEUE_QUERY_KEY, tab],
    queryFn: () => getGalleryQueue(tab),
    enabled: mine?.approver === true,
  });
  /** Reads every tab again after a photo changes list. */
  const refresh = () => queryClient.invalidateQueries({ queryKey: [...GALLERY_QUEUE_QUERY_KEY] });
  /**
   * Says that a press did not take, in the server's words where it refused.
   *
   * @param fallback - Our sentence, for a failure the server did not explain.
   */
  const failed = (fallback: string) => (err: unknown): void => {
    toast({ title: errorMessage(err, fallback), variant: 'destructive' });
  };
  /** Puts a photo on the website. */
  const approve = useMutation({
    mutationFn: approveGalleryItem,
    onSuccess: refresh,
    onError: failed(t('gallery.queue.approveFailed')),
  });
  /** Takes a photo down, or keeps it from going up. */
  const hide = useMutation({
    mutationFn: hideGalleryItem,
    onSuccess: refresh,
    onError: failed(t('gallery.queue.hideFailed')),
  });
  const busy = approve.isPending || hide.isPending;

  const heading = 'font-semibold font-body';

  if (mine && !mine.approver) {
    return (
      <div className="p-4">
        <p className="text-muted-foreground">{t('gallery.queue.notYou')}</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <Link to="/plants" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('gallery.queue.backToPlants')}
      </Link>
      <h1 className={cn(heading, 'text-2xl')}>{t('gallery.queue.title')}</h1>
      <p className="mb-4 mt-1 max-w-prose text-sm text-muted-foreground">{t('gallery.queue.intro')}</p>

      <div role="tablist" className="mb-4 flex gap-1 border-b">
        {TABS.map((status) => (
          <button
            key={status}
            role="tab"
            type="button"
            aria-selected={tab === status}
            onClick={() => setTab(status)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm',
              tab === status ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t(`gallery.queue.tab.${status}`)}
          </button>
        ))}
      </div>

      {/* A failed read is not an empty list. */}
      {isError && items.length === 0 ? (
        <p role="alert" className="py-8 text-sm text-destructive"><FailureText text={errorMessage(error, t('gallery.queue.loadFailed'))} /></p>
      ) : isLoading || !mine ? (
        <div className="flex items-center gap-3 py-8 text-sm text-muted-foreground"><Spinner size={20} />{t('gallery.queue.loading')}</div>
      ) : items.length === 0 ? (
        <p className="py-8 text-sm text-muted-foreground">{t(`gallery.queue.empty.${tab}`)}</p>
      ) : (
        // A row per photo, thumbnail-sized. A phone showed one huge picture
        // at a time and the buttons a screen away; a queue is for deciding
        // quickly, and that wants many small things, not one big one.
        <ul className="divide-y rounded-sm border bg-card">
          {items.map((item) => (
            <li key={item.id} className="flex items-center gap-3 p-2">
              <a href={item.image} target="_blank" rel="noopener" className="shrink-0" title={t('gallery.queue.openFull')}>
                <img src={item.image} alt="" className="h-20 w-20 rounded-sm object-cover" loading="lazy" />
              </a>
              <div className="min-w-0 flex-1">
                <p className={cn(heading, 'truncate text-sm')}>{item.plant}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {[item.stage, item.taken, item.by].filter(Boolean).join(' · ')}
                </p>
                {(item.city || item.state) && (
                  <p className="truncate text-xs text-muted-foreground">{[item.city, item.state].filter(Boolean).join(', ')}</p>
                )}
                <div className="mt-1.5 flex gap-2">
                  {item.status !== 'approved' && (
                    <Button size="sm" className="h-8" disabled={busy} onClick={() => approve.mutate(item.id)}>
                      <Check className="mr-1 h-3.5 w-3.5" aria-hidden="true" />{t('gallery.queue.approve')}
                    </Button>
                  )}
                  {item.status !== 'hidden' && (
                    <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => hide.mutate(item.id)}>
                      <EyeOff className="mr-1 h-3.5 w-3.5" aria-hidden="true" />{t('gallery.queue.hide')}
                    </Button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GalleryQueue;
